import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useTranslation } from '../../context/TranslationContext.jsx';
import { useAppConfig } from '../../context/AppConfigContext.jsx';
import useHealthStatus from '../../hooks/useHealthStatus';
import { usePollingManager } from '../../context/PollingManagerContext.jsx';
import { useAppContext } from '../../context/appContext.jsx';
import { normalizeBoolean } from '../../utils/normalizeBoolean';
import { getStatusIcon, getStatusTextKey } from '../HealthCheck/helpers.jsx';

import EyeTrackerStatusBanner from './EyeTrackerStatusBanner.jsx';
import PhoneStatusBanner from './PhoneStatusBanner.jsx';
import {
  StatusBannerRoot,
  StatusBannerText,
  StatusBannersRoot,
  StatusIconContainer,
} from './styles';

/** Device-readiness status bar shown directly below the header. */
const DeviceStatusBanner = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const { translate } = useTranslation();
  const { api, appName } = useAppConfig();
  const { deviceStatus, deviceStatusError } = usePollingManager();
  const { isPhoneBypassed } = useAppContext();
  const readinessUrl = api.base ? `${api.base}/readiness` : '';
  const { healthStatus, progress, statusColor } = useHealthStatus(readinessUrl, {
    updateGlobalHealthStatus: true,
  });

  const readinessText = translate(
    getStatusTextKey(healthStatus, 'settings.deviceStatus.ready', 'settings.deviceStatus.notReady'),
  );

  const phoneRequired =
    !isPhoneBypassed &&
    normalizeBoolean(
      deviceStatus?.phone_required ?? deviceStatusError?.data?.phone_required,
      false,
    );
  const errorStatusCode = deviceStatusError?.statusCode;
  const eyeTrackerStatusCode = isPhoneBypassed
    ? 200
    : (deviceStatus?.eyetracker_status_code ?? errorStatusCode ?? 0);
  const phoneStatusCode = deviceStatus?.phone_status_code ?? errorStatusCode ?? 0;
  const eyeTrackerMessage = isPhoneBypassed
    ? undefined
    : deviceStatus?.eyetracker_message ||
      deviceStatusError?.data?.eyetracker_message ||
      (errorStatusCode
        ? errorStatusCode === 500
          ? translate('statusMessages.eyetrackerNotConnected')
          : translate('statusMessages.tryReplugging')
        : undefined);
  const phoneMessage =
    deviceStatus?.phone_message ||
    deviceStatusError?.data?.phone_message ||
    (errorStatusCode
      ? errorStatusCode === 500
        ? translate('statusMessages.phoneNotConnected')
        : translate('statusMessages.phoneTakingTooLong')
      : undefined);

  return (
    <StatusBannersRoot>
      <StatusBannerRoot role="status" statusColor={statusColor}>
        <StatusIconContainer statusColor={statusColor}>
          {getStatusIcon(healthStatus, theme, isLargeScreen ? 21 : 18, progress)}
        </StatusIconContainer>
        <StatusBannerText component="span" statusColor={statusColor}>
          {translate('header.SondaDeviceIs', { appName })} {readinessText}
        </StatusBannerText>
      </StatusBannerRoot>
      <EyeTrackerStatusBanner status_code={eyeTrackerStatusCode} message={eyeTrackerMessage} />
      {phoneRequired ? (
        <PhoneStatusBanner status_code={phoneStatusCode} message={phoneMessage} />
      ) : null}
    </StatusBannersRoot>
  );
};

export default DeviceStatusBanner;
